import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { scrollElementIntoView } from "../../hooks/useAutoScrollIntoView";
import useAutoHide from "../../hooks/useAutoHide";
import MessageCountdown from "../common/MessageCountdown";
import "../../styles/IndianCalendar.css";

/** Full-page shell with Indian HR gradient background */
export function HrPageShell({ children, className = "" }) {
  return <div className={`ind-cal-page min-h-screen ${className}`}>{children}</div>;
}

/** Teal hero with tricolor accent — used on dashboards */
export function HrHero({
  eyebrow,
  title,
  subtitle,
  children,
  actions,
  stats = [],
  className = "",
}) {
  return (
    <div className={`ind-cal-hero p-6 md:p-8 mb-6 shadow-lg relative ${className}`}>
      <div className="ind-cal-hero-accent" />
      <div className="relative z-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            {eyebrow ? (
              <p className="text-white/80 text-sm font-semibold mb-2 flex items-center gap-2">
                {eyebrow}
              </p>
            ) : null}
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-100">{title}</h1>
            {subtitle ? (
              <p className="text-white/80 text-sm mt-2 max-w-2xl leading-relaxed font-medium">{subtitle}</p>
            ) : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div> : null}
        </div>
        {stats.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mt-6">
            {stats.map((s) => (
              <HrHeroStat key={s.label} label={s.label} value={s.value} accent={s.accent} />
            ))}
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}

export function HrHeroStat({ label, value, accent = "" }) {
  return (
    <div className={`ind-cal-stat px-3 py-2.5 ${accent}`}>
      <p className="text-[10px] uppercase tracking-wide text-white/75 font-semibold">{label}</p>
      <p className="text-xl md:text-2xl font-bold mt-0.5 tabular-nums">{value ?? "—"}</p>
    </div>
  );
}

export function HrPanel({ title, subtitle, children, className = "", headerRight = null, noPadding = false }) {
  return (
    <div className={`hr-panel ${className}`}>
      {(title || headerRight) && (
        <div className="hr-panel-head flex flex-wrap items-center justify-between gap-3">
          <div>
            {title ? <h2 className="hr-panel-title">{title}</h2> : null}
            {subtitle ? <p className="hr-panel-sub">{subtitle}</p> : null}
          </div>
          {headerRight}
        </div>
      )}
      <div className={noPadding ? "" : "hr-panel-body"}>{children}</div>
    </div>
  );
}

export function HrFilterBar({ children, className = "" }) {
  return <div className={`hr-filter-bar ${className}`}>{children}</div>;
}

export function HrBtn({
  children,
  variant = "primary",
  className = "",
  type = "button",
  ...props
}) {
  const variants = {
    primary: "hr-btn hr-btn-primary",
    secondary: "hr-btn hr-btn-secondary",
    ghost: "hr-btn hr-btn-ghost",
    danger: "hr-btn hr-btn-danger",
    success: "hr-btn hr-btn-success",
  };
  return (
    <button type={type} className={`${variants[variant] || variants.primary} ${className}`} {...props}>
      {children}
    </button>
  );
}

export function HrInput({ className = "", ...props }) {
  return <input className={`hr-input ${className}`} {...props} />;
}

export function HrSelect({ className = "", children, ...props }) {
  return (
    <select className={`hr-select ${className}`} {...props}>
      {children}
    </select>
  );
}

export function HrEmptyState({ icon: Icon, title, description }) {
  return (
    <div className="hr-empty">
      {Icon ? <Icon className="w-12 h-12 text-slate-300 mx-auto mb-3" /> : null}
      <p className="font-semibold text-slate-700">{title}</p>
      {description ? <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">{description}</p> : null}
    </div>
  );
}

function alertChildrenSignature(node) {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(alertChildrenSignature).join("|");
  if (typeof node === "object" && node.props) return alertChildrenSignature(node.props.children);
  return "";
}

export function HrAlert({
  variant = "info",
  children,
  className = "",
  autoScroll = true,
  scrollKey,
}) {
  const map = {
    info: "hr-alert-info",
    success: "hr-alert-success",
    error: "hr-alert-error",
    warning: "hr-alert-warning",
  };
  const ref = useRef(null);
  const signatureRef = useRef(null);
  const content =
    scrollKey != null && scrollKey !== ""
      ? String(scrollKey)
      : alertChildrenSignature(children);
  const shouldScroll =
    Boolean(autoScroll) && (variant === "error" || variant === "warning" || variant === "success");

  useEffect(() => {
    if (!shouldScroll || !content) return;
    if (signatureRef.current === content) return;
    signatureRef.current = content;
    // Defer so the alert is painted before scrolling (esp. after async actions).
    const id = window.requestAnimationFrame(() => {
      scrollElementIntoView(ref.current);
    });
    return () => window.cancelAnimationFrame(id);
  }, [shouldScroll, content]);

  // Every alert closes 30s after it appears.
  const { visible, secondsLeft, close } = useAutoHide(`${variant}|${content || "alert"}`);

  const role = variant === "error" || variant === "warning" ? "alert" : "status";

  if (!visible) return null;

  return (
    <div
      ref={ref}
      role={role}
      data-auto-scroll-alert={shouldScroll ? "true" : "false"}
      className={`hr-alert ${map[variant] || map.info} outline-none scroll-mt-6 ${className}`}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">{children}</div>
        <MessageCountdown seconds={secondsLeft} onClose={close} className="-my-1 -mr-1" />
      </div>
    </div>
  );
}

export function HrStatCard({ label, value, sub, tone = "teal", icon: Icon }) {
  return (
    <div className={`hr-stat-card hr-stat-card--${tone}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        {Icon ? <Icon className="w-4 h-4 opacity-60" /> : null}
      </div>
      <p className="text-2xl md:text-3xl font-bold mt-2 tabular-nums">{value}</p>
      {sub ? <p className="text-xs text-slate-500 mt-1">{sub}</p> : null}
    </div>
  );
}

export function HrWeekNav({ weekLabel, onPrev, onNext, prevDisabled, nextDisabled, leading }) {
  return (
    <div className="hr-week-nav">
      {leading}
      <button type="button" onClick={onPrev} disabled={prevDisabled} className="hr-week-btn" aria-label="Previous">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <div className="hr-week-label">{weekLabel}</div>
      <button type="button" onClick={onNext} disabled={nextDisabled} className="hr-week-btn" aria-label="Next">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export function HrModuleLink({ to, title, desc, icon: Icon }) {
  return (
    <Link to={to} className="hr-module-link group">
      {Icon ? (
        <div className="hr-module-link-icon">
          <Icon className="w-5 h-5" />
        </div>
      ) : null}
      <div>
        <p className="font-semibold text-slate-900 group-hover:text-teal-800 transition">{title}</p>
        <p className="text-sm text-slate-500 mt-0.5">{desc}</p>
      </div>
    </Link>
  );
}
